/**
 * LeasingExpiringWidget
 * Fordon vars leasingavtal (rent_end) löper ut inom toppnodens tröskel (default 6 mån).
 */

import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, useLanguage } from '@/store/languageStore';
import { uiDateLocale } from '@/utils/uiLocale';
import { DocumentTextIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { BaseDashboardWidget } from './BaseDashboardWidget';
import VehicleRegistrationLink from './VehicleRegistrationLink';
import { ROUTES } from '@/constants/routes';

interface LeasingExpiringVehicle {
  vehicleId: number;
  registrationNumber: string;
  make?: string;
  model?: string;
  organizationName?: string;
  rentEnd: string;
  leasingCompanyName?: string | null;
  daysUntil: number;
  isOverdue: boolean;
  procurementCaseId?: number | null;
}

interface LeasingMeta {
  totalCount: number;
  overdueCount: number;
  monthsAhead: number;
}

const LeasingExpiringWidget: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [meta, setMeta] = useState<LeasingMeta | null>(null);

  const vehicleAssignments = useMemo(
    () => ({
      categoryCode: 'LEASING',
      getVehicleId: (v: LeasingExpiringVehicle) => v.vehicleId,
      getRegistrationNumber: (v: LeasingExpiringVehicle) => v.registrationNumber,
      buildAssignmentTitle: (v: LeasingExpiringVehicle) =>
        `${String(t('leasingWidget.titleWithMonths') ?? '').replace('{months}', String(meta?.monthsAhead ?? 6))}: ${v.registrationNumber}`,
    }),
    [t, meta?.monthsAhead],
  );

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(uiDateLocale(language), {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  const getDaysLabel = (days: number) => {
    if (days < 0) {
      const absDays = Math.abs(days);
      return t('leasingWidget.daysAgo').replace('{days}', String(absDays));
    }
    if (days === 0) return t('leasingWidget.today');
    return `${days}d`;
  };

  return (
    <BaseDashboardWidget<LeasingExpiringVehicle>
      icon={DocumentTextIcon}
      colorScheme="purple"
      title={String(t('leasingWidget.titleWithMonths') ?? '').replace('{months}', String(meta?.monthsAhead ?? 6))}
      subtitle={String(t('leasingWidget.titleWithMonths') ?? '').replace('{months}', String(meta?.monthsAhead ?? 6))}
      apiEndpoint="/api/dashboard/data/upcoming-leasing-expirations"
      emptyMessage={t('leasingWidget.noVehicles')}
      getItemKey={(vehicle) => vehicle.vehicleId}
      vehicleAssignments={vehicleAssignments}
      onDataLoaded={(_, metaData) => setMeta(metaData)}
      renderBadge={() => {
        const overdueCount = meta?.overdueCount || 0;
        if (overdueCount === 0) return null;

        return (
          <span className="flex items-center gap-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-medium px-2 py-1 rounded-full">
            <ExclamationTriangleIcon className="w-3 h-3" />
            {overdueCount} {t('leasingWidget.overdue')}
          </span>
        );
      }}
      renderItem={(vehicle) => (
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-2">
              <VehicleRegistrationLink
                registrationNumber={vehicle.registrationNumber}
                vehicleId={vehicle.vehicleId}
                className="font-medium"
              />
              {vehicle.make && (
                <span className="text-xs text-textTertiary truncate">{vehicle.make}</span>
              )}
            </div>
            {vehicle.leasingCompanyName && (
              <span className="text-xs text-textTertiary truncate">{vehicle.leasingCompanyName}</span>
            )}
          </div>
          <div className="text-xs text-right shrink-0">
            <span
              className={
                vehicle.isOverdue
                  ? 'text-red-500'
                  : vehicle.daysUntil <= 30
                    ? 'text-orange-500'
                    : 'text-textTertiary'
              }
            >
              {formatDate(vehicle.rentEnd)} ({getDaysLabel(vehicle.daysUntil)})
            </span>
            {vehicle.procurementCaseId ? (
              <Link
                to={`${ROUTES.FLEET_PROCUREMENT}/${vehicle.procurementCaseId}`}
                className="block text-primary hover:underline"
              >
                {t('leasingWidget.openCase')}
              </Link>
            ) : (
              <Link to={ROUTES.FLEET_PROCUREMENT} className="block text-primary hover:underline">
                {t('leasingWidget.toModule')}
              </Link>
            )}
          </div>
        </div>
      )}
    />
  );
};

export default LeasingExpiringWidget;
